import { useState } from 'react';
import { useLoaderData, useNavigate, useRevalidator, useSearchParams } from 'react-router';
import type { LoaderFunctionArgs } from 'react-router';

import { Badge, Button, Card, Icon, KpiCard } from '@ds/core';
import { DataTable, FilterBar } from '@ds/data';
import type { DataTableColumn } from '@ds/data';
import { Alert, EmptyState } from '@ds/feedback';
import { SearchField } from '@ds/forms';
import { Pagination, Tabs } from '@ds/navigation';
import { ExportMenu } from '@/comuns/ExportMenu';
import { PageHeader } from '@/comuns/PageHeader';
import { dataEHoraPorExtenso, dataSimples, moeda } from '@/comuns/formato';
import { quantidade, unidadeLabel } from '@/comuns/unidades';
import { useAviso } from '@/comuns/toast';
import { dados } from '@/data/porta';
import { TODOS_OS_REGISTROS } from '@/comuns/exportar';
import type { AbaDeCompras, CargaDeCompras, ItemSugerido, PedidoDeCompra, SugestaoDeCompra } from '@/data/tipos';
import { rotaPorId } from '@/router/rotas';
import { useRotaAtual } from '@/router/useRotaAtual';
import { SITUACAO } from '@/screens/estoque/ProdutoDetalhe';
import { DetalheDoPedido, STATUS } from './compras/DetalheDoPedido';
import { PedidoModal } from './compras/PedidoModal';
import type { ItemSugeridoParaPedido } from './compras/PedidoModal';
import { RecebimentoModal } from './compras/RecebimentoModal';

const POR_PAGINA = 10;

const ABAS: AbaDeCompras[] = ['sugestao', 'abertos', 'recebidos', 'todos'];

function abaDaRota(pathname: string): AbaDeCompras {
    const ultimo = pathname.split('/').pop() ?? '';
    return (ABAS as string[]).includes(ultimo) ? (ultimo as AbaDeCompras) : 'sugestao';
}

export async function carregarCompras({ request }: LoaderFunctionArgs): Promise<CargaDeCompras> {
    const url = new URL(request.url);
    return dados().compras({
        aba: abaDaRota(url.pathname),
        busca: url.searchParams.get('q') ?? undefined,
        pagina: Number(url.searchParams.get('pagina') ?? '1') || 1,
        porPagina: POR_PAGINA,
    });
}

export function Compras() {
    const carga = useLoaderData() as CargaDeCompras;
    const [params, setParams] = useSearchParams();
    const navigate = useNavigate();
    const revalidator = useRevalidator();
    const handle = useRotaAtual();
    const aviso = useAviso();

    const [aberto, setAberto] = useState<PedidoDeCompra | null>(null);
    const [emEdicao, setEmEdicao] = useState<PedidoDeCompra | 'novo' | null>(null);
    const [partida, setPartida] = useState<{ fornecedorId: string; itens: ItemSugeridoParaPedido[] } | null>(null);
    const [recebendo, setRecebendo] = useState<PedidoDeCompra | null>(null);

    const rota = rotaPorId('compras');
    const aba = carga.aba;
    const subrota = handle?.subrota?.path ?? 'sugestao';

    const trocarParam = (chave: string, valor: string | null) => {
        setParams((atuais) => {
            const proximos = new URLSearchParams(atuais);
            if (!valor) proximos.delete(chave);
            else proximos.set(chave, valor);
            if (chave !== 'pagina') proximos.delete('pagina');
            return proximos;
        }, { replace: true });
    };

    const recarregar = () => revalidator.revalidate();

    /* Da sugestão para o pedido sem redigitar: é o caminho inteiro do módulo —
       o estoque avisa, a sugestão agrupa por fornecedor, o pedido sai pronto. */
    const pedirDaSugestao = (grupo: SugestaoDeCompra) => {
        setPartida({
            fornecedorId: grupo.fornecedorId ?? '',
            itens: grupo.itens.map((i) => ({
                produtoId: i.produtoId,
                quantidade: i.sugestao,
                custoUnitarioCentavos: i.custoUnitarioCentavos,
            })),
        });
        setEmEdicao('novo');
    };

    const cancelar = async (pedido: PedidoDeCompra) => {
        try {
            await dados().cancelarPedido(pedido.id);
            aviso({ title: 'Pedido cancelado', description: `${pedido.numero} · ${pedido.fornecedor}` });
            setAberto(null);
            recarregar();
        } catch (e) {
            aviso({ title: 'Não foi possível cancelar', description: e instanceof Error ? e.message : 'Tente de novo.', tone: 'danger' });
        }
    };

    const colunas: DataTableColumn<PedidoDeCompra>[] = [
        {
            key: 'numero', header: 'Pedido', sortable: true,
            render: (p) => (
                <span>
                    <strong style={{ display: 'block', fontWeight: 600 }}>{p.numero}</strong>
                    <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{p.fornecedor}</span>
                </span>
            ),
        },
        {
            key: 'itens', header: 'Itens',
            render: (p) => (
                <span style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 12.5 }}>
                    {p.itens.map((i) => (
                        <span key={i.id}>{quantidade(i.quantidade)} {unidadeLabel(i.quantidade, i.unidade)} · {i.produto}</span>
                    ))}
                </span>
            ),
        },
        {
            key: 'criadoEm', header: 'Criado', sortable: true,
            render: (p) => (
                <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>{dataEHoraPorExtenso(p.criadoEm)}</span>
                    {p.criadoPor && <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{p.criadoPor}</span>}
                </span>
            ),
        },
        {
            key: 'previsaoEntrega', header: 'Previsão', sortable: true,
            render: (p) => (p.previsaoEntrega ? dataSimples(p.previsaoEntrega) : <span style={{ color: 'var(--text-tertiary)' }}>—</span>),
        },
        { key: 'status', header: 'Situação', render: (p) => <Badge tone={STATUS[p.status].tom} dot>{STATUS[p.status].rotulo}</Badge> },
        {
            key: 'totalCentavos', header: 'Total', align: 'right',
            render: (p) => (
                <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    <strong style={{ fontWeight: 600 }}>{moeda(p.totalCentavos)}</strong>
                    {p.status === 'parcial' && (
                        <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{moeda(p.recebidoCentavos)} recebidos</span>
                    )}
                </span>
            ),
        },
        {
            key: 'acoes', header: '', align: 'right', width: 120,
            render: (p) => (
                <span data-parar-linha>
                    {(p.status === 'enviado' || p.status === 'parcial') && (
                        <Button size="sm" variant="secondary" onClick={() => setRecebendo(p)}>Receber</Button>
                    )}
                    {p.status === 'rascunho' && (
                        <Button size="sm" variant="ghost" onClick={() => setEmEdicao(p)}>Editar</Button>
                    )}
                </span>
            ),
        },
    ];

    const colunasDaSugestao = (grupo: SugestaoDeCompra): DataTableColumn<ItemSugerido>[] => [
        { key: 'produto', header: 'Produto', render: (i) => i.produto },
        {
            key: 'disponivel', header: 'Disponível', align: 'right',
            render: (i) => (
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {quantidade(i.disponivel)} {unidadeLabel(i.disponivel, i.unidade)}
                </span>
            ),
        },
        { key: 'minimo', header: 'Mínimo', align: 'right', render: (i) => quantidade(i.minimo) },
        { key: 'situacao', header: 'Situação', render: (i) => <Badge tone={SITUACAO[i.situacao].tom} dot>{SITUACAO[i.situacao].rotulo}</Badge> },
        {
            key: 'sugestao', header: 'Comprar', align: 'right',
            render: (i) => (
                <strong style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                    {quantidade(i.sugestao)} {unidadeLabel(i.sugestao, i.unidade)}
                </strong>
            ),
        },
        {
            key: 'custoUnitarioCentavos', header: 'Custo', align: 'right',
            render: (i) => `${moeda(i.sugestao * i.custoUnitarioCentavos)} (${grupo.fornecedor})`,
        },
    ];

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <PageHeader
                title="Compras"
                description="O que repor, o pedido ao fornecedor e a entrada no estoque quando chega"
                actions={
                    <>
                        {aba !== 'sugestao' && (
                            <ExportMenu<PedidoDeCompra>
                                label="Exportar" size="md"
                                carregarTudo={async () => (await dados().compras({
                                    aba, busca: params.get('q') ?? undefined, pagina: 1, porPagina: TODOS_OS_REGISTROS,
                                })).pedidos}
                                payload={{
                                    title: 'Pedidos de compra', subtitle: `${carga.total} pedidos`, rows: carga.pedidos,
                                    columns: [
                                        { header: 'Pedido', value: (p) => p.numero },
                                        { header: 'Fornecedor', value: (p) => p.fornecedor },
                                        { header: 'Situação', value: (p) => STATUS[p.status].rotulo },
                                        { header: 'Previsão', value: (p) => (p.previsaoEntrega ? dataSimples(p.previsaoEntrega) : '') },
                                        { header: 'Itens', value: (p) => p.itens.map((i) => `${quantidade(i.quantidade)} ${unidadeLabel(i.quantidade, i.unidade)} ${i.produto}`).join(' | ') },
                                        { header: 'Total', value: (p) => moeda(p.totalCentavos) },
                                        { header: 'Recebido', value: (p) => moeda(p.recebidoCentavos) },
                                    ],
                                }}
                            />
                        )}
                        <Button
                            iconLeft={<Icon name="plus" size={15} />}
                            onClick={() => { setPartida(null); setEmEdicao('novo'); }}
                        >
                            Novo pedido
                        </Button>
                    </>
                }
            >
                <Tabs
                    ariaLabel="Seções de compras" activeId={subrota}
                    onChange={(id) => navigate({ pathname: `/app/compras/${id}`, search: params.get('q') ? `?q=${params.get('q')}` : '' })}
                    tabs={(rota?.children ?? []).map((c) => ({
                        id: c.path,
                        label: c.label,
                        count: c.path === 'sugestao' ? carga.totais.sugeridos
                            : c.path === 'abertos' ? carga.totais.abertos
                                : c.path === 'recebidos' ? carga.totais.recebidos
                                    : carga.totais.todos,
                    }))}
                />
            </PageHeader>

            <div className="vec-stagger" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(170px,1fr))', gap: 12 }}>
                {carga.indicadores.map((i) => (
                    <KpiCard key={i.id} label={i.rotulo} value={i.valor} hint={i.apoio} deltaTone={i.tendencia} icon={<Icon name={i.icone} size={16} />} />
                ))}
            </div>

            {aba === 'sugestao' ? (
                <>
                    {carga.sugestoes.length === 0 ? (
                        <Card padding={0}>
                            <EmptyState
                                icon="package-check"
                                title="Nada a repor"
                                description="Nenhum produto está abaixo do mínimo. A sugestão volta a aparecer sozinha quando o estoque cair."
                            />
                        </Card>
                    ) : (
                        <>
                            <Alert tone="info" dismissible={false} title="Como a sugestão é calculada">
                                Entram os produtos abaixo do mínimo, agrupados pelo fornecedor de cada um. A quantidade
                                leva ao <strong>dobro do mínimo</strong>, em embalagens inteiras: comprar exatamente o
                                mínimo faz o alerta voltar na primeira saída.
                            </Alert>

                            {carga.sugestoes.map((grupo) => (
                                <Card
                                    key={grupo.fornecedorId ?? grupo.fornecedor}
                                    title={grupo.fornecedor}
                                    subtitle={`${grupo.itens.length} ${grupo.itens.length === 1 ? 'item' : 'itens'} · ${moeda(grupo.totalCentavos)}`}
                                    padding={0}
                                    actions={(
                                        <Button
                                            size="sm" iconLeft={<Icon name="truck" size={14} />}
                                            disabled={!grupo.fornecedorId}
                                            /* O nome do fornecedor vai no rótulo acessível: são
                                               vários "Criar pedido" na mesma tela, e quem ouve
                                               precisa saber qual é qual. */
                                            aria-label={`Criar pedido para ${grupo.fornecedor}`}
                                            onClick={() => pedirDaSugestao(grupo)}
                                        >
                                            Criar pedido
                                        </Button>
                                    )}
                                >
                                    <DataTable columns={colunasDaSugestao(grupo)} rows={grupo.itens} rowKey="produtoId" />
                                </Card>
                            ))}
                        </>
                    )}
                </>
            ) : (
                <>
                    <FilterBar
                        search={(
                            <SearchField
                                value={params.get('q') ?? ''} onChange={(v) => trocarParam('q', v || null)} width={280}
                                placeholder="Buscar por número, fornecedor ou produto"
                            />
                        )}
                        resultLabel={`${carga.total} ${carga.total === 1 ? 'pedido' : 'pedidos'}`}
                    />

                    <DataTable
                        columns={colunas} rows={carga.pedidos} rowKey="id"
                        onRowClick={setAberto} rowLabel={(p) => `${p.numero} · ${p.fornecedor}`}
                        emptyMessage={aba === 'abertos' ? 'Nenhum pedido em aberto.' : 'Nenhum pedido neste recorte.'}
                    />

                    <Pagination
                        page={carga.pagina} pageCount={carga.paginas} total={carga.total} pageSize={POR_PAGINA}
                        onChange={(p) => trocarParam('pagina', String(p))}
                    />
                </>
            )}

            <DetalheDoPedido
                pedido={aberto}
                onClose={() => setAberto(null)}
                onEditar={(p) => { setAberto(null); setPartida(null); setEmEdicao(p); }}
                onReceber={(p) => { setAberto(null); setRecebendo(p); }}
                onCancelar={(p) => void cancelar(p)}
            />

            <PedidoModal
                alvo={emEdicao} partida={partida} fornecedores={carga.fornecedores} produtos={carga.produtos}
                onClose={() => { setEmEdicao(null); setPartida(null); }}
                onSalvo={recarregar}
            />

            <RecebimentoModal pedido={recebendo} onClose={() => setRecebendo(null)} onRecebido={recarregar} />
        </div>
    );
}
